import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import { API } from "../lib/access";
import { profile } from "../data/content";

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// Owner-only page: /admin?key=<ADMIN_KEY>. Visitor log + (dormant) access requests.
export default function Admin() {
  const [params] = useSearchParams();
  const [key, setKey] = useState(params.get("key") || sessionStorage.getItem("rm_admin_key") || "");
  const [keyInput, setKeyInput] = useState("");
  const [requests, setRequests] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [error, setError] = useState("");
  const [links, setLinks] = useState({}); // request id -> freshly issued access URL
  const [copiedId, setCopiedId] = useState("");

  const load = useCallback(async (k) => {
    try {
      const [reqRes, visRes] = await Promise.all([
        fetch(`${API}/admin/access-requests?key=${encodeURIComponent(k)}`),
        fetch(`${API}/admin/visitors?key=${encodeURIComponent(k)}`),
      ]);
      if (!reqRes.ok || !visRes.ok) {
        setError("That admin key didn't work.");
        setRequests(null);
        setVisitors(null);
        return;
      }
      setError("");
      setRequests(await reqRes.json());
      setVisitors(await visRes.json());
    } catch {
      setError("Could not load data.");
    }
  }, []);

  useEffect(() => {
    if (key) {
      sessionStorage.setItem("rm_admin_key", key);
      load(key);
    }
  }, [key, load]);

  const approve = async (id) => {
    const r = await fetch(`${API}/admin/access-requests/${id}/approve?key=${encodeURIComponent(key)}`, { method: "POST" });
    if (r.ok) {
      const data = await r.json();
      setLinks((m) => ({ ...m, [id]: data.access_url }));
      load(key);
    }
  };

  const revoke = async (id) => {
    await fetch(`${API}/admin/access-requests/${id}/revoke?key=${encodeURIComponent(key)}`, { method: "POST" });
    load(key);
  };

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 2000);
    } catch {
      window.prompt("Copy this link:", text);
    }
  };

  const statusChip = (status) =>
    status === "approved"
      ? "bg-panel text-body"
      : status === "revoked"
        ? "bg-panel text-faint line-through"
        : "bg-teal-light text-teal";

  return (
    <div className="min-h-screen bg-canvas text-ink font-sans antialiased" data-testid="admin-page">
      <header className="border-b border-hairline">
        <div className="container-x h-16 flex items-center justify-between">
          <span className="w-9 h-9 border border-cardline bg-panel flex items-center justify-center font-display text-sm font-semibold">
            {profile.initials}
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-faint">Admin</span>
        </div>
      </header>

      <main className="container-x py-16 max-w-4xl">
        {!key ? (
          <div className="max-w-sm">
            <p className="font-display text-2xl font-semibold">Enter your admin key.</p>
            <form
              className="mt-6 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setKey(keyInput.trim());
              }}
            >
              <input
                data-testid="admin-key-input"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin key"
                className="flex-1 bg-canvas border border-cardline px-4 py-3 text-sm focus:outline-none focus:border-teal transition-colors"
              />
              <button
                data-testid="admin-key-submit"
                className="bg-teal text-canvas text-sm font-medium px-5 hover:bg-teal-deep transition-colors"
              >
                Open
              </button>
            </form>
          </div>
        ) : error ? (
          <p className="text-sm text-red-800" data-testid="admin-error">{error}</p>
        ) : visitors === null ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint">Loading…</p>
        ) : (
          <>
            {/* Visitor log — everyone who signed in with Google */}
            <section data-testid="admin-visitors">
              <div className="flex items-baseline gap-3 mb-6">
                <h2 className="font-display text-2xl font-semibold tracking-tight">Who's signed in</h2>
                <span className="font-mono text-xs text-faint">{visitors.length} visit{visitors.length === 1 ? "" : "s"}</span>
              </div>
              {visitors.length === 0 ? (
                <p className="text-base text-body" data-testid="visitors-empty-state">
                  No sign-ins yet. When someone views the portfolio, they'll appear here.
                </p>
              ) : (
                <div className="space-y-px bg-hairline border border-hairline">
                  {visitors.map((v, i) => (
                    <div key={i} className="bg-canvas px-6 py-4 flex items-center gap-4" data-testid={`visitor-row-${i}`}>
                      {v.picture ? (
                        <img src={v.picture} alt="" className="w-8 h-8 rounded-full border border-cardline" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="w-8 h-8 rounded-full border border-cardline bg-panel flex items-center justify-center font-mono text-[10px] text-faint">
                          {(v.name || v.email || "?").slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{v.name || "—"}</p>
                        <p className="text-xs text-faint truncate">{v.email}</p>
                      </div>
                      <span className="font-mono text-[11px] tracking-wide text-faint shrink-0">{fmtTime(v.signed_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Dormant access-request tools */}
            <section className="mt-16 pt-10 border-t border-hairline" data-testid="admin-requests">
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Access requests</h2>
              {requests === null || requests.length === 0 ? (
                <p className="text-base text-body" data-testid="admin-empty-state">
                  No requests yet. When someone asks for access, they'll appear here.
                </p>
              ) : (
                <div className="space-y-px bg-hairline border border-hairline">
                  {requests.map((r, i) => (
                    <div key={r.id} className="bg-canvas p-6 sm:p-7" data-testid={`admin-request-row-${i}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="font-medium text-ink">
                            {r.name} <span className="text-faint font-normal">· {r.email}</span>
                          </p>
                          {r.note && <p className="mt-1.5 text-sm text-body leading-relaxed max-w-xl">{r.note}</p>}
                          <p className="mt-2 font-mono text-[11px] tracking-wide text-faint">
                            {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${statusChip(r.status)}`}>
                            {r.status}
                          </span>
                          {r.status !== "revoked" && (
                            <button
                              data-testid={`approve-button-${i}`}
                              onClick={() => approve(r.id)}
                              className="text-sm font-medium text-canvas bg-teal px-4 py-2 hover:bg-teal-deep transition-colors"
                            >
                              {r.status === "approved" ? "Re-issue link" : "Approve"}
                            </button>
                          )}
                          {r.status === "approved" && (
                            <button
                              data-testid={`revoke-button-${i}`}
                              onClick={() => revoke(r.id)}
                              className="text-sm text-faint hover:text-red-800 transition-colors"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>

                      {links[r.id] && (
                        <div className="mt-5 bg-teal-light border border-teal/30 p-4 flex flex-wrap items-center gap-3" data-testid={`access-link-${i}`}>
                          <code className="text-xs text-teal-deep break-all flex-1 min-w-[200px]">{links[r.id]}</code>
                          <button
                            data-testid={`copy-link-${i}`}
                            onClick={() => copy(r.id, links[r.id])}
                            className="inline-flex items-center gap-2 text-xs font-medium text-canvas bg-teal px-3 py-2 hover:bg-teal-deep transition-colors"
                          >
                            {copiedId === r.id ? <Check size={13} /> : <Copy size={13} />}
                            {copiedId === r.id ? "Copied" : "Copy link"}
                          </button>
                          <p className="w-full text-xs text-body">
                            Send this to {r.name.split(" ")[0]} — it's their personal way in. Approving again replaces the old link.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
